import { useCallback, useMemo, useState } from 'preact/hooks';

import { TFunction, useTranslation } from '../../../utils/useTranslation';
import ns from '../translations';

import { usePlayer } from './PlayerProvider';
import { useJWPlayerEvent } from './useJWPlayerEvent';

const createQualityList = (levels: jwplayer.Level[], t: TFunction) => {
  return levels.map((level, index) => {
    return {
      label: t(level.label),
      value: index,
    };
  });
};

export const useQualityLevels = () => {
  const { t } = useTranslation(ns);
  const player = usePlayer();
  const jw = player.getJWPlayer();

  const [availableQualities, setAvailableQualities] = useState(
    jw?.getQualityLevels() ?? []
  );
  const [selectedQuality, setSelectedQuality] = useState(
    jw?.getCurrentQuality() ?? 0
  );

  const qualitiesList = useMemo(
    () => (availableQualities ? createQualityList(availableQualities, t) : []),
    [availableQualities, t]
  );

  const selectQuality = useCallback(
    (value: number) => {
      jw?.setCurrentQuality(value);
    },
    [jw]
  );

  const handleLevels = useCallback((param: jwplayer.LevelsParam) => {
    setAvailableQualities(param.levels);
  }, []);

  const handleLevelsChanged = useCallback(
    (param: jwplayer.LevelsChangedParam) => {
      setSelectedQuality(param.currentQuality);
    },
    []
  );

  useJWPlayerEvent('levels', handleLevels);
  useJWPlayerEvent('levelsChanged', handleLevelsChanged);

  // Safari Desktop and iOS does not provide any way to change quality of an HLS stream.
  // jwplayer reference https://github.com/jwplayer/jwplayer/blob/master/src/js/view/controls/components/menu/settings-menu.js#L33

  const hasQualities = qualitiesList.length > 1;

  return {
    qualitiesList,
    selectedQuality,
    selectQuality,
    hasQualities,
  };
};
